// Admin API client pro komunikaci s backend serverem

export interface AppConfig {
  [key: string]: string
}

export interface RaynetSource {
  id: number
  name: string
  valid: boolean
}

export interface RaynetCategory {
  id: number
  name: string
  valid: boolean
}

export interface LeadHistory {
  id: number
  raynet_id: number
  lead_data: Record<string, unknown> // Better than any
  created_at: string
  status: 'success' | 'error'
  error_message?: string
}

export interface RaynetStatus {
  status: 'ok' | 'error'
  statusCode?: number
  message: string
  url?: string
  error?: string
}

export interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface LeadsResponse {
  data: LeadHistory[]
  pagination: PaginationInfo
}

export interface OfflineRequest {
  id: number
  request_type: string
  payload: Record<string, unknown>
  created_at: string
  status: 'pending' | 'sent' | 'failed'
  retry_count: number
  error_message?: string
  last_attempt?: string
}

export interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface LeadsResponse {
  data: LeadHistory[]
  pagination: PaginationInfo
}

class AdminClient {
  private baseUrl = '/api'
  private password: string | null = null

  setPassword(password: string) {
    this.password = password
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (this.password) {
      headers.Authorization = `Bearer ${this.password}`
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(
        errorData?.error || `HTTP ${response.status}: ${response.statusText}`
      )
    }

    return response.json()
  }

  // Health check - newyžaduje autentizaci
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health')
  }

  // Konfigurace
  async getConfig(): Promise<AppConfig> {
    return this.request('/config')
  }

  async setConfig(key: string, value: string): Promise<{ success: boolean }> {
    return this.request('/config', {
      method: 'POST',
      body: JSON.stringify({ key, value }),
    })
  }

  // Raynet zdroje
  async getSources(): Promise<RaynetSource[]> {
    return this.request('/sources')
  }

  async syncSources(): Promise<{ success: boolean; count: number; message: string }> {
    return this.request('/sources/sync', {
      method: 'POST',
    })
  }

  // Raynet kategorie
  async getCategories(): Promise<RaynetCategory[]> {
    return this.request('/categories')
  }

  async syncCategories(): Promise<{ success: boolean; count: number; message: string }> {
    return this.request('/categories/sync', {
      method: 'POST',
    })
  }

  // Lead historie
  async getLeads(page: number = 1, limit: number = 10): Promise<LeadsResponse> {
    return this.request(`/leads?page=${page}&limit=${limit}`)
  }

  async saveLeadHistory(
    raynetId: number | null,
    leadData: Record<string, unknown>,
    status: 'success' | 'error',
    errorMessage?: string
  ): Promise<{ success: boolean }> {
    return this.request('/leads', {
      method: 'POST',
      body: JSON.stringify({
        raynet_id: raynetId,
        lead_data: leadData,
        status,
        error_message: errorMessage,
      }),
    })
  }

  // Raynet API status
  async getRaynetStatus(): Promise<RaynetStatus> {
    return this.request('/raynet-status')
  }

  // Offline queue management
  async getOfflineQueue(): Promise<OfflineRequest[]> {
    return this.request('/offline-queue')
  }

  async processOfflineRequest(id: number): Promise<{ success: boolean; message: string; raynetId?: number }> {
    return this.request(`/offline-queue/${id}/process`, {
      method: 'POST',
    })
  }

  async processAllOfflineRequests(): Promise<{ 
    success: boolean; 
    message: string; 
    processed: number; 
    failed: number; 
    errors?: string[] 
  }> {
    return this.request('/offline-queue/process-all', {
      method: 'POST',
    })
  }

  async deleteOfflineRequest(id: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/offline-queue/${id}`, {
      method: 'DELETE',
    })
  }
}

export const adminClient = new AdminClient() 